
import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { buildIcon, Icon } from "@cervval.js/client-html5-core/src/components/models/icon";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { toggleClass } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { PADDING } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { AcceptedHtmlElementContent, appendHTMLElementContent, empty, setHTMLElementContent } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { StringTools } from "@cervval.js/common/tools";
import { ButtonIconMode } from "../button/button";
import { CjsIcon } from "../icon/icon";

export const CJS_ACCORDION_CLASSNAME = "cjs-accordion";

export interface AccordionOptions {

    /** Label of accordion */
    label: string;

    /** Id of parent accordionBodyContainer */
    parentId: string | undefined;

    /** Default is collapsed */
    collapsed: boolean;

    /** allows the accordion to stay open when another one is opened */
    alwaysOpened: boolean;

    /** Icon of button */
    icon?: Icon | CjsIcon;

    /** Set label position, after or before button */
    iconMode?: ButtonIconMode;
}

export const defaultAccordionOptions: AccordionOptions = {
    /** Label of accordion */
    label: "",

    /** Id of parent accordionBodyContainer */
    parentId: undefined,

    /** Default is collapsed */
    collapsed: true,

    /** allows the accordion to stay open when another one is opened */
    alwaysOpened: false,

    iconMode: "before"
};

@CustomElement({
    name: "cjs-accordion",
    template: require("./accordion.html"),
    style: require("./accordion.scss"),
    elementCssClasses: ["accordion"],
    bindChildrenSlots: true
})
export class CjsAccordion extends WebComponent {

    @TypedWebComponentOptions(defaultAccordionOptions)
    public readonly options!: WebComponentOptions<AccordionOptions>;

    /** event fired when accordion in opened or closed */
    @Event(NoBubblesEventInit)
    public toggleEvent!: EventHandler<{ open: boolean }>;

    /** 
     * The components Accordion Section
     */
    @TypedHtmlElements()
    public readonly components!: {
        accordionHeading: HTMLDivElement,
        toggleBtn: HTMLDivElement,
        accordionBodyContainer: HTMLDivElement,
        accordionBody: HTMLSlotElement,
        accordionItem: HTMLDivElement,
        toggleBtnLabel: HTMLSlotElement,
        icon: HTMLElement
    };

    constructor(options?: Partial<AccordionOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    /** When component is initialized */
    public override init(): void {

        this.id = "accordion" + this.componentNumber;

        this.components.accordionBodyContainer.id = "accordionBodyContainer" + this.componentNumber;
        this.components.toggleBtn.setAttribute("aria-controls", this.components.accordionBodyContainer.id);
        this.components.toggleBtn.setAttribute("data-bs-target", "#" + this.components.accordionBodyContainer.id);
        this.components.toggleBtn.setAttribute("data-bs-toggle", "collapse");

        this.components.accordionHeading.id = "accordionHeading" + this.componentNumber;
        this.components.accordionBodyContainer.setAttribute("aria-labelledby", this.components.accordionHeading.id);

        this.components.accordionBodyContainer.addEventListener("show.bs.collapse", () => {
            this.toggleEvent.emit({ open: true });
        });
        this.components.accordionBodyContainer.addEventListener("hide.bs.collapse", () => {
            this.toggleEvent.emit({ open: false });
        });
    }

    /** When component should refresh his display */
    public override refresh(): void {

        if (!this.options.alwaysOpened) {
            // ! The way this component is built imply that an accordion without a parent will, whatever the case, be always opened
            // ! An accordion without a parent can't close itself when another accordion in the same hierarchy is opened
            if (this.options.parentId != null) {
                this.components.accordionBodyContainer.setAttribute("data-bs-parent", "#" + this.options.parentId);
            }
        }

        this.components.toggleBtnLabel.innerText = StringTools.capitalizeFirstLetter(this.options.label);

        if (this.options.collapsed) {
            this.components.toggleBtn.classList.add("collapsed");
            this.components.accordionBodyContainer.classList.remove("show");
        } else {
            this.components.toggleBtn.classList.remove("collapsed");
            this.components.accordionBodyContainer.classList.add("show");
        }

        this._refreshIcon();
    }

    /** Set content in body accordion */
    public setContent(...contents: AcceptedHtmlElementContent[]): void {
        setHTMLElementContent(this.components.accordionBody, ...contents);
    }

    /** Add content in body accordion */
    public addContent(...contents: AcceptedHtmlElementContent[]): void {
        appendHTMLElementContent(this.components.accordionBody, ...contents);
    }

    /** Open accordion */
    public open(): void {
        if (!this.isOpen()) {
            this.components.toggleBtn.click();
        }
    }

    /** Close accordion */
    public close(): void {
        if (this.isOpen()) {
            this.components.toggleBtn.click();
        }
    }

    /** Is accordion currently open */
    public isOpen(): boolean {
        return !this.components.toggleBtn.classList.contains("collapsed");
    }

    /**
     * refresh accordion icon
     * Set icon layout
     */
    protected _refreshIcon(): void {
        let iconComponent: HTMLElement = this.components.icon;
        const iconOption = this.options.icon;

        if (iconOption) {
            if (iconOption instanceof CjsIcon) {
                iconComponent = iconOption;
            } else {
                iconComponent.className = buildIcon(iconOption).className;
                if (iconOption.htmlString != null) {
                    iconComponent.innerHTML = iconOption.htmlString;
                }
            }

            // Set label position
            if (this.options.iconMode === "before") {
                this.components.toggleBtnLabel.prepend(iconComponent);
                toggleClass(iconComponent, Boolean(this.options.label), PADDING.PE_2);
                iconComponent.classList.remove(PADDING.PS_2);

                // After icon
            } else if (this.options.iconMode === "after") {
                this.components.toggleBtnLabel.append(iconComponent);
                toggleClass(iconComponent, Boolean(this.options.label), PADDING.PS_2);
                iconComponent.classList.remove(PADDING.PE_2);

                // Icon only: Remove text if icon only is set
            } else if (this.options.iconMode === "only") {
                // Remove previous classes from other iconMode
                iconComponent.classList.remove(PADDING.PS_2, PADDING.PE_2);
                empty(this.components.toggleBtnLabel);
                this.components.toggleBtnLabel.append(iconComponent);
            }

            iconComponent.classList.remove("hidden-at-init");
        }
    }
}